import axiosInstance from './axios';

// FMLA Doctor API Types - EXACT MATCH with backend models

// Enums for better type safety
export enum AppointmentStatus {
  AVAILABLE = 'available',
  BOOKED = 'booked',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum ConsultationType {
  FMLA = 'fmla',
  FMLA_STD = 'fmla-std'
}

export enum UserType {
  PATIENT = 'patient',
  PROVIDER = 'provider'
}

// User Profile Interface - matches backend util/models.go User struct exactly
export interface UserProfile {
  email: string;                         // Primary key (matches Go json:"email")
  name: string;                          // User's full name (matches Go json:"name") 
  createdAt: string;                     // When account was created (matches Go json:"createdAt")
  lastLogin?: string;                    // Last login timestamp (matches Go json:"lastLogin,omitempty")
  userType: UserType;                    // "patient" | "provider" (matches Go json:"userType")
  // Patient-specific fields
  employmentState?: string;              // US state for provider matching (matches Go json:"employmentState,omitempty")
  serviceType?: string;                  // "fmla" | "fmla-std" | "follow-up" (matches Go json:"serviceType,omitempty")
  paymentAmount?: number;                // Amount paid for service - Go int becomes JS number (matches Go json:"paymentAmount,omitempty")
  // Provider-specific fields
  licensedStates?: string[];             // States where licensed (matches Go json:"licensedStates,omitempty")
  providerKey?: string;                  // Provider invitation key (matches Go json:"providerKey,omitempty")
  onboardingStatus?: string;             // Provider onboarding status (matches Go json:"onboardingStatus,omitempty")
  // Account status
  isActive?: boolean;                    // Account active status (matches Go json:"isActive,omitempty")
  // HIPAA retention
  ttl?: number;                          // 7-year retention (matches Go json:"ttl,omitempty") - Go int64 becomes JS number
}

// Appointment Interface - EXACT MATCH with backend util/models.go Appointment struct
export interface Appointment {
  appointmentId: string;                 // Primary key UUID (matches Go json:"appointmentId")
  patientEmail: string | null;           // null = available slot (matches Go *string json:"patientEmail,omitempty")
  providerEmail: string;                 // Provider assignment (matches Go json:"providerEmail")
  dateTime: string;                      // ISO datetime (matches Go json:"dateTime")
  serviceType: ConsultationType;         // "fmla" | "fmla-std" (matches Go json:"serviceType")
  duration: number;                      // Duration in minutes (matches Go int json:"duration") 
  status: AppointmentStatus;             // "available" | "booked" | "in-progress" | "completed" | "cancelled" (matches Go json:"status")
  cancelledBy?: string;                  // "patient" | "provider" (matches Go json:"cancelledBy,omitempty")
  // Timestamps - EXACT Go struct match
  createdAt: string;                     // (matches Go json:"createdAt")
  bookedAt?: string | null;              // (matches Go *string json:"bookedAt,omitempty")
  startTime?: string | null;             // (matches Go *string json:"startTime,omitempty")
  endTime?: string | null;               // (matches Go *string json:"endTime,omitempty")
  cancelledAt?: string | null;           // (matches Go *string json:"cancelledAt,omitempty")
  // Chime SDK video consultation - EXACT Go struct match
  meetingId?: string | null;             // (matches Go *string json:"meetingId,omitempty")
  meetingArn?: string | null;            // (matches Go *string json:"meetingArn,omitempty")
  consultationStatus?: string;           // "ready" | "active" | "completed" (matches Go json:"consultationStatus,omitempty")
  mediaRegion?: string;                  // (matches Go json:"mediaRegion,omitempty")
  presenceData?: Record<string, any>;    // (matches Go map[string]interface{} json:"presenceData,omitempty")
  // Clinical workflow - EXACT Go struct match
  consultationNotes?: string;            // (matches Go json:"consultationNotes,omitempty")
  patientDocuments?: PatientDocument[];  // Embedded array (matches Go []PatientDocument json:"patientDocuments,omitempty")
  generatedForms?: FMLAForm[];           // Embedded array (matches Go []FMLAForm json:"generatedForms,omitempty")
  fmlaCertificationGenerated?: boolean;  // (matches Go bool json:"fmlaCertificationGenerated,omitempty")
  followUpRequired?: boolean;            // (matches Go bool json:"followUpRequired,omitempty")
  // HIPAA retention
  ttl?: number;                          // 7-year retention (matches Go int64 json:"ttl,omitempty")
}

// PatientDocument - EXACT MATCH with backend util/models.go PatientDocument struct
export interface PatientDocument {
  fileName: string;                      // (matches Go json:"fileName")
  s3Key: string;                         // (matches Go json:"s3Key")
  fileSize: number;                      // (matches Go int64 json:"fileSize") - Go int64 becomes JS number
  uploadedAt: string;                    // (matches Go json:"uploadedAt")
}

// FMLAForm - EXACT MATCH with backend util/models.go FMLAForm struct
export interface FMLAForm {
  formType: string;                      // "fmla-initial" | "fmla-extension" | "std-form" (matches Go json:"formType")
  s3Key: string;                         // (matches Go json:"s3Key")
  generatedAt: string;                   // (matches Go json:"generatedAt")
  formData?: Record<string, any>;        // (matches Go map[string]interface{} json:"formData,omitempty")
}

// Create Appointment Request - for creating new appointments with auto provider assignment
export interface CreateAppointmentRequest {
  dateTime: string;                  // ISO datetime for the appointment
  duration: number;                  // Duration in minutes (30, 60, 90)
  consultationType: ConsultationType; // "fmla" | "fmla-std"
  notes?: string;                    // Optional notes from patient
}

// Schedule Appointment Request - EXACT MATCH with backend ScheduleAppointmentRequest
export interface ScheduleAppointmentRequest {
  appointmentId: string; // Book specific available slot
}

// Cancel Appointment Request - EXACT MATCH with backend CancelAppointmentRequest 
export interface CancelAppointmentRequest {
  cancelledBy: string; // "patient" | "provider"
}

// Document Upload Request - matches backend uploadDocument handler
export interface DocumentUploadRequest {
  fileName: string;
  fileSize: number;
  contentType: string;
}

// Document Upload Response - matches backend response
export interface DocumentUploadResponse {
  uploadUrl: string;
  s3Key: string;
  documentId: string;
  expiresAt: string;
}

// Document Download Response - matches backend response
export interface DocumentDownloadResponse {
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
}

// Video Consultation Interfaces - EXACT MATCH with backend
export interface CreateAttendeeRequest {
  userType: 'patient' | 'provider';
}

export interface MeetingResponse {
  meetingId: string;
  mediaRegion: string;
  meeting?: any; // Chime SDK meeting object
  attendee?: any; // Chime SDK attendee object
}

// =============================================================================
// FMLA Doctor API - 7 Core Endpoints (Miller's Law 7±2)
// =============================================================================

// 1. User Management API
export const userAPI = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await axiosInstance.get('/user');
    return response.data;
  },
  
  updateProfile: async (updates: { name?: string; employmentState?: string }): Promise<{ message: string }> => {
    const response = await axiosInstance.put('/user', updates);
    return response.data;
  },
};

// 2. Appointment API - MATCHES backend handlers exactly
export const appointmentAPI = {
  // GET /appointments - listAppointments handler with query parameters for slot discovery
  list: async (params?: { status?: string; state?: string }): Promise<Appointment[]> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.state) queryParams.append('state', params.state);
    
    const url = queryParams.toString() ? `/appointments?${queryParams.toString()}` : '/appointments';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  // Specific method for slot discovery
  getAvailableSlots: async (state?: string): Promise<Appointment[]> => {
    const response = await axiosInstance.get(`/appointments?status=available${state ? `&state=${state}` : ''}`);
    return response.data;
  },

  // Alias for backward compatibility
  getAppointments: async (): Promise<Appointment[]> => {
    const response = await axiosInstance.get('/appointments');
    return response.data;
  },

  // GET /appointments/{id} - getAppointment handler  
  get: async (appointmentId: string): Promise<Appointment> => {
    const response = await axiosInstance.get(`/appointments/${appointmentId}`);
    return response.data;
  },

  // POST /appointments/create - createAppointment handler (creates new appointment with auto provider assignment)
  createAppointment: async (request: CreateAppointmentRequest): Promise<Appointment> => {
    const response = await axiosInstance.post('/appointments/create', request);
    return response.data;
  },

  // POST /appointments - scheduleAppointment handler (books available slot)
  book: async (request: ScheduleAppointmentRequest): Promise<Appointment> => {
    const response = await axiosInstance.post('/appointments', request);
    return response.data;
  },

  // PUT /appointments/{id} - updateAppointment handler (notes, status)
  update: async (appointmentId: string, updates: {
    consultationNotes?: string;
    status?: string;
  }): Promise<{ message: string }> => {
    const response = await axiosInstance.put(`/appointments/${appointmentId}`, updates);
    return response.data;
  },

  // DELETE /appointments/{id} - cancelAppointment handler
  cancel: async (appointmentId: string, reason?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/appointments/${appointmentId}`, {
      data: { reason }
    });
    return response.data;
  },

  // Alias for backward compatibility
  cancelAppointment: async (appointmentId: string, reason?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/appointments/${appointmentId}`, {
      data: { reason }
    });
    return response.data;
  },

  // 3. Video Consultation API - MATCHES backend handlers exactly
  // POST /appointments/{id}/meeting - createMeeting handler
  createMeeting: async (appointmentId: string): Promise<MeetingResponse> => {
    const response = await axiosInstance.post(`/appointments/${appointmentId}/meeting`);
    return response.data;
  },

  // POST /appointments/{id}/attendee - createAttendee handler
  joinMeeting: async (appointmentId: string, request: CreateAttendeeRequest): Promise<MeetingResponse> => {
    const response = await axiosInstance.post(`/appointments/${appointmentId}/attendee`, request);
    return response.data;
  },

  // POST /appointments/{id}/attendee - createAttendee handler (alias for backwards compatibility)
  createAttendee: async (appointmentId: string, request: CreateAttendeeRequest): Promise<MeetingResponse> => {
    const response = await axiosInstance.post(`/appointments/${appointmentId}/attendee`, request);
    return response.data;
  },

  // GET /appointments/{id}/meeting - getMeetingInfo handler
  getMeetingInfo: async (appointmentId: string): Promise<MeetingResponse> => {
    const response = await axiosInstance.get(`/appointments/${appointmentId}/meeting`);
    return response.data;
  },
};

// 4. Document API - APPOINTMENT-EMBEDDED MODEL (matches backend)
export const documentAPI = {
  // POST /appointments/{id}/upload - uploadDocument handler (creates presigned URL)
  createUploadUrl: async (appointmentId: string, request: DocumentUploadRequest): Promise<DocumentUploadResponse> => {
    const response = await axiosInstance.post(`/appointments/${appointmentId}/upload`, request);
    return response.data;
  },

  // GET /appointments/{id}/documents - listAppointmentDocuments handler
  listForAppointment: async (appointmentId: string): Promise<PatientDocument[]> => {
    const response = await axiosInstance.get(`/appointments/${appointmentId}/documents`);
    return response.data;
  },

  // GET /appointments/{id}/documents/{docId} - downloadDocument handler
  getDownloadUrl: async (appointmentId: string, documentId: string): Promise<DocumentDownloadResponse> => {
    const response = await axiosInstance.get(`/appointments/${appointmentId}/documents/${documentId}`);
    return response.data;
  },

  // DELETE /appointments/{id}/documents/{docId} - deleteDocument handler
  delete: async (appointmentId: string, documentId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/appointments/${appointmentId}/documents/${documentId}`);
    return response.data;
  },

  uploadFile: async (appointmentId: string, file: File): Promise<void> => {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds the 50MB limit');
    }

    const uploadResponse = await documentAPI.createUploadUrl(appointmentId, {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
    });

    // Step 2: Upload file to S3 using presigned URL
    await fetch(uploadResponse.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    // Note: No confirmation step needed - backend embeds document in appointment immediately
  },
};

// 5. FMLA Forms API - MATCHES backend fmla-forms lambda endpoints
export const formsAPI = {
  listForAppointment: async (appointmentId: string): Promise<FMLAForm[]> => {
    const response = await axiosInstance.get(`/forms/${appointmentId}`);
    return response.data;
  },

  getDownloadUrl: async (appointmentId: string, formId: string): Promise<{ downloadUrl: string; expiresAt: string }> => {
    const response = await axiosInstance.get(`/forms/${appointmentId}/download/${formId}`);
    return response.data;
  },
};

// =============================================================================
// SIMPLIFIED API EXPORTS - 7 Core Endpoints Following Miller's Law
// =============================================================================

// Export simplified API with exactly 8 core functions (updated for slot discovery)
export const api = {
  // 1. User profile
  getProfile: userAPI.getProfile,
  updateProfile: userAPI.updateProfile,
  
  // 2. Appointments (core workflow)
  listAppointments: appointmentAPI.list,
  getAvailableSlots: appointmentAPI.getAvailableSlots,
  bookAppointment: appointmentAPI.book,
  cancelAppointment: appointmentAPI.cancel,
  
  // 3. Video consultation
  startVideoCall: appointmentAPI.createMeeting,
  joinVideoCall: appointmentAPI.joinMeeting,
  
  // Note: Document upload/download accessed through appointment context
};

// Note: appointmentAPI, documentAPI, and userAPI are already exported above as const exports